import { BRAND } from "@garageregister/shared";
import {
  Scan,
  FileText,
  Wrench,
  Puzzle,
  FolderKanban,
  MapPin,
  Rss,
  UserCircle,
  Gauge,
  CircleDot,
  Camera,
  Eye,
  History,
  Users,
} from "lucide-react";

const features = [
  { icon: Scan, title: "AI Document Scanning", description: "Snap a registration, insurance card, emissions doc, dyno sheet, time slip, or service receipt — AI extracts the details so you don’t have to type. Free tier includes 5 lifetime scans.", highlight: true },
  { icon: FileText, title: "Glovebox AI", description: "Insurance, registration, emissions, inspection, toll transponders, car wash memberships — all in one place, all auto-filled from a photo.", highlight: true },
  { icon: Wrench, title: "Service Logs", description: "Track every oil change, brake job, and shop visit with dates, mileage, parts, and costs. AI scans receipts to fill in the details.", highlight: true },
  { icon: Puzzle, title: "Mod Tracking", description: "Document every modification — parts, vendors, installers, before/after. AI reads invoices so you don’t type a thing.", highlight: true },
  { icon: Gauge, title: "Performance Logs", description: "Dyno runs, drag strip times, lap times, 0-60 sprints, and roll races. AI reads your sheets and slips.", highlight: true },
  { icon: CircleDot, title: "Wheels & Tires", description: "Track wheel sets, tire specs, and pressure. Swap between setups and keep a history of every set you’ve run.", highlight: false },
  { icon: Camera, title: "Photo Gallery", description: "Upload photos with 12 garage-themed filters. Crop, zoom, and showcase your build the way you want it seen.", highlight: false },
  { icon: FolderKanban, title: "Projects", description: "Manage full builds with task lists, parts tracking, timelines, and shared access for the friends helping out.", highlight: false },
  { icon: MapPin, title: "Trips", description: "Plan multi-stop drives with day legs, lunch stops, and overnight stays. Log mileage and capture photos along the way.", highlight: false },
  { icon: History, title: "Past Rides", description: "Every car, truck, or bike you’ve owned belongs in your garage — even the ones you sold. Past rides don’t count against the free vehicle limit.", highlight: false },
  { icon: Users, title: "Meets", description: "Find local car meets — Cars & Coffees, weekly cruise nights, ad-hoc lot meets. RSVP and spot for free; check-ins are Pro and write to your vehicle’s history.", highlight: false },
  { icon: Eye, title: "Spotted", description: "See a build in the wild? Snap it and it lands in the public feed in seconds. Free for everyone.", highlight: false },
  { icon: Rss, title: "Social Feed", description: "Follow other builders. Rev their posts, leave threaded comments, and @mention users or ride handles.", highlight: false },
  { icon: UserCircle, title: "Public Profiles & Ride Handles", description: "Get a @handle profile page and claim ride handles like @lava-911. Every vehicle gets its own URL at /r/<handle> — share it anywhere.", highlight: false },
];

export function FeatureShowcase() {
  return (
    <section id="features" className="bg-[#0C1222] py-20 sm:py-28">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 className="font-heading text-3xl font-bold text-[#E8EDF2] sm:text-4xl">
            Everything your garage needs
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-[#7A8FA3]">
            From routine oil changes to full engine swaps, {BRAND.name} keeps
            every detail organized and searchable.
          </p>
        </div>

        <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((feature) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.title}
                className={`group relative rounded-xl border p-6 transition-all duration-300 hover:-translate-y-1 ${
                  feature.highlight
                    ? "border-[#4DB8C7]/30 bg-[#4DB8C7]/5 hover:border-[#4DB8C7]/50 hover:shadow-[0_0_30px_rgba(77,184,199,0.15)]"
                    : "border-[#253448] bg-[#151D2E] hover:border-[#4DB8C7]/30 hover:bg-[#1E2B3D]"
                }`}
              >
                {feature.highlight && (
                  <div className="absolute -top-3 right-4">
                    <span className="rounded-full bg-[#4DB8C7] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#0C1222]">
                      AI Powered
                    </span>
                  </div>
                )}
                <div
                  className={`mb-4 inline-flex rounded-lg p-2.5 ${
                    feature.highlight ? "bg-[#4DB8C7]/10" : "bg-[#253448]"
                  }`}
                >
                  <Icon
                    className={`h-5 w-5 ${
                      feature.highlight
                        ? "text-[#4DB8C7]"
                        : "text-[#7A8FA3] group-hover:text-[#4DB8C7]"
                    } transition-colors`}
                  />
                </div>
                <h3 className="font-heading text-base font-semibold text-[#E8EDF2]">
                  {feature.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-[#7A8FA3]">
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
